/**
 * useOfflineStatus Hook
 * React hook for detecting online/offline status in Electron app
 */

import { useState, useEffect, useCallback } from "react";

/**
 * Hook to track online/offline status
 * @returns {{ isOnline: boolean, isElectron: boolean, pendingCount: number, syncError: string|null, forceCheck: function }}
 */
export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncError, setSyncError] = useState(null);
  const [isElectron, setIsElectron] = useState(false);

  // Check initial status
  useEffect(() => {
    const electronAPI = window.electronAPI;

    if (electronAPI?.isElectron) {
      setIsElectron(true);

      // Get initial online status
      if (electronAPI.offline?.getStatus) {
        electronAPI.offline
          .getStatus()
          .then((status) => {
            setIsOnline(status);
          })
          .catch((err) => {
            console.error("[useOfflineStatus] Error getting status:", err);
            setIsOnline(true);
          });
      }

      // Get initial pending count
      if (electronAPI.offline?.getPendingCount) {
        electronAPI.offline
          .getPendingCount()
          .then((count) => {
            setPendingCount(count);
          })
          .catch((err) => {
            console.error(
              "[useOfflineStatus] Error getting pending count:",
              err
            );
          });
      }

      // Subscribe to status changes
      let cleanup = null;
      let cleanupSync = null;
      let cleanupQueue = null;
      if (electronAPI.offline?.onStatusChange) {
        cleanup = electronAPI.offline.onStatusChange((newStatus) => {
          console.log("[useOfflineStatus] Status changed:", newStatus);
          setIsOnline(newStatus);

          // Update pending count when status changes
          if (electronAPI.offline?.getPendingCount) {
            electronAPI.offline.getPendingCount().then((count) => {
              setPendingCount(count);
            });
          }
        });
      }

      // A successful reconnect sync changes the queue without changing the
      // network status, so refresh the badge when sync completes as well.
      if (electronAPI.sync?.onSyncCompleted) {
        cleanupSync = electronAPI.sync.onSyncCompleted((result) => {
          if (result?.direction === "to-server") {
            setSyncError(
              result.success
                ? null
                : result.errors?.[0]?.error || result.error || "Không thể đồng bộ đơn hàng",
            );
          }
          if (electronAPI.offline?.getPendingCount) {
            electronAPI.offline.getPendingCount().then(setPendingCount);
          }
        });
      }
      if (electronAPI.offline?.onQueueChange) {
        cleanupQueue = electronAPI.offline.onQueueChange(setPendingCount);
      }

      // Cleanup on unmount
      return () => {
        if (cleanup && typeof cleanup === "function") {
          cleanup();
        }
        if (cleanupSync && typeof cleanupSync === "function") {
          cleanupSync();
        }
        if (cleanupQueue && typeof cleanupQueue === "function") {
          cleanupQueue();
        }
      };
    } else {
      // Fallback for browser - use navigator.onLine
      setIsOnline(navigator.onLine);

      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);

      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }
  }, []);

  // Force check status
  const forceCheck = useCallback(async () => {
    const electronAPI = window.electronAPI;

    if (electronAPI?.offline?.forceCheck) {
      const status = await electronAPI.offline.forceCheck();
      setIsOnline(status);

      // Also update pending count
      if (electronAPI.offline?.getPendingCount) {
        const count = await electronAPI.offline.getPendingCount();
        setPendingCount(count);
      }

      return status;
    }

    return navigator.onLine;
  }, []);

  // Refresh pending count
  const refreshPendingCount = useCallback(async () => {
    const electronAPI = window.electronAPI;

    if (electronAPI?.offline?.getPendingCount) {
      const count = await electronAPI.offline.getPendingCount();
      setPendingCount(count);
      return count;
    }

    return 0;
  }, []);

  return {
    isOnline,
    isElectron,
    pendingCount,
    syncError,
    forceCheck,
    refreshPendingCount,
  };
}

export default useOfflineStatus;
